import React, { useEffect, useRef, useState } from 'react';
import {
  App,
  Button,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { TableColumnsType } from 'antd';
import { AppstoreAddOutlined, CheckOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  CustomerCategory,
  CustomerCategoryAssignedCustomer,
  categoryErrorMessage,
  createCustomerCategoryTag,
  deleteCustomerCategoryTag,
  fetchCustomerCategoryAssignmentCandidates,
  fetchCustomerCategoryAssignments,
  fetchCustomerCategoryTags,
  replaceCustomerCategoryAssignments,
  updateCustomerCategoryTag
} from '../../modules/customers/customerCategoriesApi';
import { useFeedback } from '../../utils/feedback';

const { Text } = Typography;
const { TextArea } = Input;

// Preset swatches so the saved value is always a valid #rrggbb hex (matches the
// backend COLOR_PATTERN). "none" clears the color.
const COLOR_PRESETS = ['#1677ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#8c8c8c'];
const CUSTOMER_PREVIEW_NAME_MAX_LENGTH = 36;

interface CustomerCategoriesTabProps {
  canEdit: boolean;
  /** Called after any create/update/delete so the parent can refresh selects/filters. */
  onCategoriesChanged?: () => void;
}

interface CategoryFormValues {
  name: string;
  description?: string | null;
  color?: string | null;
}

type AssignmentCustomerOption = CustomerCategoryAssignedCustomer & {
  customerCategoryName?: string | null;
};

const buildCustomerAssignmentSearchLabel = (customer: CustomerCategoryAssignedCustomer) => [
  customer.name,
  customer.email,
  customer.taxId,
  customer.address
].filter(Boolean).join(' ');

function getCustomerPreviewName(customer: CustomerCategoryAssignedCustomer): string {
  const name = customer.name.trim();
  if (name.length <= CUSTOMER_PREVIEW_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, CUSTOMER_PREVIEW_NAME_MAX_LENGTH - 1).trimEnd()}…`;
}

const CustomerCategoriesTab: React.FC<CustomerCategoriesTabProps> = ({ canEdit, onCategoriesChanged }) => {
  const { t } = useTranslation(['customers', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [categories, setCategories] = useState<CustomerCategory[]>([]);
  const [customers, setCustomers] = useState<CustomerCategoryAssignedCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerCategory | null>(null);
  const [assigningCategory, setAssigningCategory] = useState<CustomerCategory | null>(null);
  const [assignmentCustomerOptions, setAssignmentCustomerOptions] = useState<AssignmentCustomerOption[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentReady, setAssignmentReady] = useState(false);
  const [assignmentSelectOpen, setAssignmentSelectOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const assignmentSessionRef = useRef(0);
  const [form] = Form.useForm<CategoryFormValues>();

  const loadCategories = async () => {
    setLoading(true);
    try {
      setCategories(await fetchCustomerCategoryTags());
    } catch (error) {
      feedback.error(t('customers:customerCategory.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      setCustomers(await fetchCustomerCategoryAssignmentCandidates());
    } catch (error) {
      feedback.error(t('customers:customerCategory.customersLoadFailed'));
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (category: CustomerCategory) => {
    setEditing(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description ?? undefined,
      color: category.color ?? undefined
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  };

  const mergeAssignmentOptions = (
    allCustomers: CustomerCategoryAssignedCustomer[],
    assignedCustomers: CustomerCategoryAssignedCustomer[],
    category: CustomerCategory
  ) => {
    const byId = new Map<string, AssignmentCustomerOption>();
    allCustomers.forEach((customer) => byId.set(customer.id, customer));
    assignedCustomers.forEach((customer) => {
      byId.set(customer.id, {
        ...byId.get(customer.id),
        ...customer,
        customerCategoryName: byId.get(customer.id)?.customerCategoryName ?? category.name
      });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  };

  const openAssignments = async (category: CustomerCategory) => {
    const sessionId = assignmentSessionRef.current + 1;
    assignmentSessionRef.current = sessionId;
    setAssigningCategory(category);
    setSelectedCustomerIds([]);
    setAssignmentCustomerOptions([]);
    setAssignmentReady(false);
    setAssignmentSelectOpen(false);
    setAssignmentModalOpen(true);
    setAssignmentLoading(true);
    try {
      const [freshCustomers, assignedCustomers] = await Promise.all([
        fetchCustomerCategoryAssignmentCandidates(),
        fetchCustomerCategoryAssignments(category.id)
      ]);
      if (assignmentSessionRef.current !== sessionId) return;
      setCustomers(freshCustomers);
      setAssignmentCustomerOptions(mergeAssignmentOptions(freshCustomers, assignedCustomers, category));
      setSelectedCustomerIds(assignedCustomers.map((customer) => customer.id));
      setAssignmentReady(true);
    } catch (error) {
      if (assignmentSessionRef.current !== sessionId) return;
      feedback.error(t('customers:customerCategory.assignmentLoadFailed', { name: category.name }));
    } finally {
      if (assignmentSessionRef.current === sessionId) {
        setAssignmentLoading(false);
      }
    }
  };

  const closeAssignmentModal = () => {
    if (savingAssignments) return;
    assignmentSessionRef.current += 1;
    setAssignmentModalOpen(false);
    setAssigningCategory(null);
    setAssignmentCustomerOptions([]);
    setSelectedCustomerIds([]);
    setAssignmentReady(false);
    setAssignmentSelectOpen(false);
  };

  const handleSubmit = async (values: CategoryFormValues) => {
    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        color: values.color || null
      };
      if (editing) {
        await updateCustomerCategoryTag(editing.id, payload);
        feedback.success(t('customers:customerCategory.updatedSuccess', { name: payload.name }));
      } else {
        await createCustomerCategoryTag(payload);
        feedback.success(t('customers:customerCategory.createdSuccess', { name: payload.name }));
      }
      setModalOpen(false);
      setEditing(null);
      await loadCategories();
      await loadCustomers();
      onCategoriesChanged?.();
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('customers:customerCategory.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!assigningCategory) return;
    if (!assignmentReady) {
      feedback.error(t('customers:customerCategory.assignmentLoadFailed', { name: assigningCategory.name }));
      return;
    }

    setSavingAssignments(true);
    try {
      await replaceCustomerCategoryAssignments(assigningCategory.id, selectedCustomerIds);
      feedback.success(
        t('customers:customerCategory.assignmentsSaved', {
          name: assigningCategory.name,
          count: selectedCustomerIds.length
        })
      );
      setAssignmentModalOpen(false);
      setAssigningCategory(null);
      setSelectedCustomerIds([]);
      await Promise.all([loadCategories(), loadCustomers()]);
      onCategoriesChanged?.();
    } catch (error: any) {
      feedback.error(
        categoryErrorMessage(
          error,
          t,
          t('customers:customerCategory.assignFailed', { name: assigningCategory.name })
        )
      );
    } finally {
      setSavingAssignments(false);
    }
  };

  const handleDelete = (category: CustomerCategory) => {
    const customerCount = category.customerCount ?? 0;
    modal.confirm({
      title: t('customers:customerCategory.deleteConfirmTitle'),
      content: customerCount > 0
        ? t('customers:customerCategory.deleteConfirmWithCustomers', { count: customerCount })
        : t('customers:customerCategory.deleteConfirm'),
      okText: t('common:yes'),
      cancelText: t('common:no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingId(category.id);
        try {
          await deleteCustomerCategoryTag(category.id);
          feedback.success(t('customers:customerCategory.deletedSuccess', { name: category.name }));
          await Promise.all([loadCategories(), loadCustomers()]);
          onCategoriesChanged?.();
        } catch (error: any) {
          feedback.error(
            categoryErrorMessage(
              error,
              t,
              t('customers:customerCategory.deleteFailed', { name: category.name })
            )
          );
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const getCategoryCustomers = (categoryId: string) =>
    customers.filter((customer) => customer.customerCategoryId === categoryId);

  const renderCustomerPreview = (categoryId: string) => {
    const assignedCustomers = getCategoryCustomers(categoryId);
    if (assignedCustomers.length === 0) {
      return <Text type="secondary">{t('customers:customerCategory.noAssignedCustomers')}</Text>;
    }

    const visibleCustomers = assignedCustomers.slice(0, 3);
    return (
      <Space size={[4, 4]} wrap>
        {visibleCustomers.map((customer) => (
          <Tooltip key={customer.id} title={customer.name}>
            <Tag>{getCustomerPreviewName(customer)}</Tag>
          </Tooltip>
        ))}
        {assignedCustomers.length > visibleCustomers.length && (
          <Text type="secondary">
            {t('customers:customerCategory.customersPreviewMore', {
              count: assignedCustomers.length - visibleCustomers.length
            })}
          </Text>
        )}
      </Space>
    );
  };

  const columns: TableColumnsType<CustomerCategory> = [
    {
      title: t('customers:customerCategory.name'),
      key: 'name',
      render: (_value, record) => (
        <Tag color={record.color || 'default'} style={{ fontSize: 14, padding: '2px 10px' }}>
          {record.name}
        </Tag>
      )
    },
    {
      title: t('customers:customerCategory.description'),
      dataIndex: 'description',
      key: 'description',
      render: (description: string | null) => description || <Text type="secondary">—</Text>
    },
    {
      title: t('customers:customerCategory.customerCount'),
      key: 'customerCount',
      width: 160,
      align: 'center',
      render: (_value, record) => <Tag>{record.customerCount ?? 0}</Tag>
    },
    {
      title: t('customers:customerCategory.assignedCustomers'),
      key: 'assignedCustomers',
      render: (_value, record) => renderCustomerPreview(record.id)
    }
  ];

  if (canEdit) {
    columns.push({
      title: '',
      key: 'actions',
      width: 154,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('customers:customerCategory.assignCustomers')}>
            <Button
              size="small"
              icon={<AppstoreAddOutlined />}
              onClick={() => openAssignments(record)}
            />
          </Tooltip>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      )
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16 }}>
        <Text type="secondary">{t('customers:customerCategory.tabHint')}</Text>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('customers:customerCategory.add')}
          </Button>
        )}
      </div>

      <Table<CustomerCategory>
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading || customersLoading}
        pagination={false}
        locale={{ emptyText: <Empty description={t('customers:customerCategory.empty')} /> }}
      />

      <Modal
        title={editing ? t('customers:customerCategory.edit') : t('customers:customerCategory.create')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={saving}
        maskClosable={false}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} preserve={false}>
          <Form.Item
            name="name"
            label={t('customers:customerCategory.name')}
            rules={[{ required: true, message: t('customers:customerCategory.nameRequired') }]}
          >
            <Input
              placeholder={t('customers:customerCategory.namePlaceholder')}
              maxLength={120}
            />
          </Form.Item>

          <Form.Item name="description" label={t('customers:customerCategory.description')}>
            <TextArea
              rows={3}
              maxLength={500}
              placeholder={t('customers:customerCategory.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item name="color" label={t('customers:customerCategory.color')}>
            <Select allowClear placeholder={t('customers:customerCategory.colorPlaceholder')}>
              {COLOR_PRESETS.map((color) => (
                <Select.Option key={color} value={color}>
                  <Space>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: color
                      }}
                    />
                    {color}
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={assigningCategory
          ? t('customers:customerCategory.assignTitle', { name: assigningCategory.name })
          : t('customers:customerCategory.assignCustomers')}
        open={assignmentModalOpen}
        onCancel={closeAssignmentModal}
        onOk={handleAssignmentSubmit}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingAssignments}
        okButtonProps={{ disabled: !assignmentReady || assignmentLoading }}
        maskClosable={false}
        width={720}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text type="secondary">{t('customers:customerCategory.assignHelp')}</Text>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('customers:customerCategory.assignPlaceholder')}
            value={selectedCustomerIds}
            onChange={setSelectedCustomerIds}
            open={assignmentSelectOpen}
            onOpenChange={setAssignmentSelectOpen}
            loading={assignmentLoading}
            disabled={!assignmentReady || assignmentLoading}
            style={{ width: '100%' }}
            maxTagCount="responsive"
            popupRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0' }} />
                <Button
                  type="primary"
                  block
                  icon={<CheckOutlined />}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setAssignmentSelectOpen(false)}
                >
                  {t('customers:customerCategory.selectionDone', {
                    count: selectedCustomerIds.length,
                  })}
                </Button>
              </>
            )}
          >
            {assignmentCustomerOptions.map((customer) => (
              <Select.Option
                key={customer.id}
                value={customer.id}
                label={buildCustomerAssignmentSearchLabel(customer)}
              >
                <Space direction="vertical" size={0}>
                  <Text>{customer.name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {[
                      customer.taxId,
                      customer.customerCategoryName
                        ? t('customers:customerCategory.currentlyInCategory', { name: customer.customerCategoryName })
                        : t('customers:customerCategory.none')
                    ].filter(Boolean).join(' · ')}
                  </Text>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Modal>
    </div>
  );
};

export default CustomerCategoriesTab;
